import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Globe, Lock, RotateCcw, Sparkles, Wrench } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import AnimateIn from '../AnimateIn';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const scoreColor = (s) => (s >= 75 ? '#4ade80' : s >= 50 ? '#e2c179' : '#f87171');

const ScoreGauge = ({ score }) => {
  const r = 80;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-[200px] h-[200px]" data-testid="audit-score-gauge">
      <svg width="200" height="200" viewBox="0 0 200 200" className="-rotate-90">
        <circle cx="100" cy="100" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
        <motion.circle
          cx="100" cy="100" r={r} fill="none"
          stroke={scoreColor(score)} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c * (1 - score / 100) }}
          transition={{ duration: 1.6, ease: [0.25, 0.1, 0.25, 1], delay: 0.3 }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className="text-5xl font-bold text-white"
          style={{ fontFamily: "'Syne', sans-serif" }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          data-testid="audit-score-value"
        >
          {score}
        </motion.span>
        <span className="text-xs tracking-[0.2em] uppercase text-gray-500">/ 100</span>
      </div>
    </div>
  );
};

const CategoryRow = ({ cat, index }) => (
  <motion.div
    className="rounded-xl border border-white/10 bg-white/[0.03] p-5"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.08 }}
    data-testid={`audit-category-${index}`}
  >
    <div className="flex items-center justify-between mb-3">
      <p className="text-sm font-semibold text-white">{cat.name}</p>
      <span className="text-sm font-bold" style={{ color: scoreColor(cat.score) }}>{cat.score}</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-4">
      <motion.div
        className="h-full rounded-full"
        style={{ background: scoreColor(cat.score) }}
        initial={{ width: 0 }}
        whileInView={{ width: `${cat.score}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.2 + index * 0.08 }}
      />
    </div>
    <ul className="space-y-2">
      {(cat.issues || []).map((issue, i) => (
        <li key={i} className="flex items-start gap-2 text-[13px] leading-relaxed text-gray-400">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" style={{ color: '#e2c179' }} />
          {issue}
        </li>
      ))}
    </ul>
  </motion.div>
);

const AuditSection = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [stepIdx, setStepIdx] = useState(0);
  const [unlocked, setUnlocked] = useState(false);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadStatus, setLeadStatus] = useState('idle');
  const resultsRef = useRef(null);

  const steps = [t('auditStep1'), t('auditStep2'), t('auditStep3'), t('auditStep4')];

  useEffect(() => {
    if (status !== 'loading') return;
    setStepIdx(0);
    const id = setInterval(() => setStepIdx((s) => Math.min(s + 1, 3)), 7000);
    return () => clearInterval(id);
  }, [status]);

  const runAudit = async (e) => {
    e.preventDefault();
    if (!url.trim() || status === 'loading') return;
    setStatus('loading');
    setErrorMsg('');
    setResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim(), language }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(res.status === 422 || res.status === 400 ? t('auditErrorReach') : t('auditErrorGeneric'));
        setStatus('error');
        return;
      }
      setResult(data);
      setStatus('done');
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } catch {
      setErrorMsg(t('auditErrorGeneric'));
      setStatus('error');
    }
  };

  const reset = () => {
    setStatus('idle');
    setResult(null);
    setUrl('');
    setUnlocked(false);
    setLeadEmail('');
    setLeadStatus('idle');
  };

  const unlockReport = async (e) => {
    e.preventDefault();
    const email = leadEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLeadStatus('error');
      return;
    }
    setLeadStatus('sending');
    try {
      const res = await fetch(`${BACKEND_URL}/api/audit-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, url: result?.url || '', score: result?.score, language }),
      });
      if (!res.ok) throw new Error();
      setUnlocked(true);
      setLeadStatus('idle');
    } catch {
      setLeadStatus('error');
    }
  };

  const talkPrefill = result
    ? language === 'fr'
      ? `Je viens de lancer l'audit IA sur ${result.url} (note ${result.score}/100). J'aimerais discuter des correctifs recommand\u00E9s.`
      : `I just ran the AI audit on ${result.url} (score ${result.score}/100). I'd like to discuss fixing the issues found.`
    : '';

  return (
    <section id="ws-audit" className="py-20 md:py-28 px-6 lg:px-8 scroll-mt-24" data-testid="audit-section">
      <div className="max-w-[1200px] mx-auto">
        <AnimateIn direction="up" duration={0.8}>
          <div
            className="relative overflow-hidden rounded-3xl border border-white/10 p-8 md:p-16"
            style={{ background: 'radial-gradient(ellipse at 80% -20%, rgba(226,193,121,0.14) 0%, #0a0a0a 55%)' }}
          >
            <div
              className="absolute inset-0 opacity-[0.03] pointer-events-none"
              style={{
                backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
                backgroundSize: '48px 48px',
              }}
            />
            <div className="relative z-10">
              <div className="max-w-2xl">
                <p className="accent-tag">
                  <Sparkles size={13} className="inline mr-1.5 -mt-0.5" />
                  {t('auditTag')}
                </p>
                <h2
                  className="text-3xl md:text-5xl font-bold tracking-[-0.02em] text-white mb-5"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                  data-testid="audit-title"
                >
                  {t('auditTitle')}
                </h2>
                <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-10">{t('auditSubtitle')}</p>
              </div>

              {/* Input */}
              {status !== 'done' && (
                <form onSubmit={runAudit} className="max-w-2xl" data-testid="audit-form">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Globe size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type="text"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder={t('auditPlaceholder')}
                        className="form-input !pl-11"
                        disabled={status === 'loading'}
                        data-testid="audit-url-input"
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn-accent whitespace-nowrap justify-center"
                      disabled={status === 'loading' || !url.trim()}
                      data-testid="audit-submit-btn"
                    >
                      {status === 'loading' ? t('auditStep1').replace('...', '') : t('auditButton')}
                      {status !== 'loading' && <ArrowRight size={16} />}
                    </button>
                  </div>
                  {status === 'error' && (
                    <p className="mt-4 text-sm" style={{ color: '#fca5a5' }} data-testid="audit-error-message">
                      {errorMsg}
                    </p>
                  )}
                </form>
              )}

              {/* Loading state */}
              <AnimatePresence>
                {status === 'loading' && (
                  <motion.div
                    className="mt-12 max-w-2xl"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    data-testid="audit-loading"
                  >
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="relative w-10 h-10">
                          <div className="absolute inset-0 rounded-full border-2 border-white/10" />
                          <div
                            className="absolute inset-0 rounded-full border-2 border-transparent animate-spin"
                            style={{ borderTopColor: '#e2c179' }}
                          />
                        </div>
                        <AnimatePresence mode="wait">
                          <motion.p
                            key={stepIdx}
                            className="text-white text-sm font-medium"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.4 }}
                            data-testid="audit-loading-step"
                          >
                            {steps[stepIdx]}
                          </motion.p>
                        </AnimatePresence>
                      </div>
                      <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ background: 'linear-gradient(90deg, #706958, #e2c179)' }}
                          initial={{ width: '5%' }}
                          animate={{ width: `${20 + stepIdx * 25}%` }}
                          transition={{ duration: 6, ease: 'easeOut' }}
                        />
                      </div>
                      <p className="mt-4 text-xs text-gray-500">{t('auditWait')}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Results */}
              {status === 'done' && result && (
                <motion.div
                  ref={resultsRef}
                  className="mt-4 scroll-mt-28"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  data-testid="audit-results"
                >
                  {/* Score + verdict */}
                  <div className="flex flex-col md:flex-row items-center gap-8 md:gap-14 mb-12 rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10">
                    <ScoreGauge score={result.score} />
                    <div className="text-center md:text-left flex-1">
                      <p className="text-[11px] font-semibold tracking-[0.25em] uppercase text-gray-500 mb-2">
                        {t('auditScoreLabel')} · {result.url?.replace(/^https?:\/\//, '')} · {result.pages_scanned} {t('auditPagesScanned')}
                      </p>
                      <p
                        className="text-xl md:text-2xl font-semibold text-white leading-snug"
                        style={{ fontFamily: "'Syne', sans-serif" }}
                        data-testid="audit-verdict"
                      >
                        {result.verdict}
                      </p>
                      <button
                        className="mt-5 inline-flex items-center gap-2 text-xs tracking-wide text-gray-500 hover:text-gray-300 transition-colors"
                        onClick={reset}
                        data-testid="audit-reset-btn"
                      >
                        <RotateCcw size={13} />
                        {t('auditAnother')}
                      </button>
                    </div>
                  </div>

                  {/* Gated report */}
                  <div className="relative">
                    <div
                      className={unlocked ? '' : 'pointer-events-none select-none'}
                      style={unlocked ? {} : { filter: 'blur(16px)', maxHeight: 560, overflow: 'hidden' }}
                      aria-hidden={!unlocked}
                      data-testid="audit-report-content"
                    >
                  {/* Categories */}
                  <h3
                    className="text-lg font-semibold text-white mb-5"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    {t('auditBreakdown')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-testid="audit-categories">
                    {(result.categories || []).map((cat, i) => (
                      <CategoryRow key={i} cat={cat} index={i} />
                    ))}
                  </div>

                  {/* Top fixes */}
                  <h3
                    className="text-lg font-semibold text-white mb-5"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    {t('auditTopFixes')}
                  </h3>
                  <div className="space-y-3 mb-12 max-w-3xl" data-testid="audit-top-fixes">
                    {(result.top_fixes || []).map((fix, i) => (
                      <div key={i} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4">
                        <span
                          className="text-xs font-bold mt-0.5 shrink-0"
                          style={{ color: '#e2c179', fontFamily: "'Syne', sans-serif" }}
                        >
                          0{i + 1}
                        </span>
                        <p className="text-sm text-gray-300 leading-relaxed">{fix}</p>
                      </div>
                    ))}
                  </div>

                  {/* Recommendations */}
                  <div
                    className="rounded-2xl border p-8 md:p-10"
                    style={{ borderColor: 'rgba(226,193,121,0.25)', background: 'linear-gradient(135deg, rgba(112,105,88,0.15), rgba(10,10,10,0.6))' }}
                    data-testid="audit-recommendations"
                  >
                    <p className="accent-tag">
                      <Wrench size={13} className="inline mr-1.5 -mt-0.5" />
                      {t('auditRecoTitle')}
                    </p>
                    <p className="text-gray-400 text-sm md:text-base mb-8 max-w-2xl">{t('auditRecoSub')}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                      {(result.recommendations || []).map((rec, i) => (
                        <div key={i} className="rounded-xl border border-white/10 bg-black/30 p-5" data-testid={`audit-reco-${i}`}>
                          <p className="text-sm font-semibold text-white mb-1.5" style={{ color: '#e2c179' }}>{rec.service}</p>
                          <p className="text-[13px] text-gray-400 leading-relaxed">{rec.reason}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <button
                        className="btn-accent w-full sm:w-auto justify-center"
                        onClick={() => navigate('/services/customize')}
                        data-testid="audit-cta-build"
                      >
                        {t('auditCtaBuild')}
                        <ArrowRight size={16} />
                      </button>
                      <button
                        className="btn-outline w-full sm:w-auto justify-center"
                        onClick={() => navigate('/contact', { state: { prefill: talkPrefill } })}
                        data-testid="audit-cta-talk"
                      >
                        {t('auditCtaTalk')}
                      </button>
                      <button
                        className="btn-outline w-full sm:w-auto justify-center"
                        onClick={() => {
                          reset();
                          setTimeout(() => document.getElementById('ws-audit')?.scrollIntoView({ behavior: 'smooth' }), 100);
                        }}
                        data-testid="audit-another-btn"
                      >
                        <RotateCcw size={15} />
                        {t('auditAnother')}
                      </button>
                    </div>
                  </div>
                    </div>
                    {!unlocked && (
                      <div
                        className="absolute inset-0 z-10 flex items-start justify-center pt-8 md:pt-14"
                        style={{ background: 'linear-gradient(180deg, rgba(5,5,5,0) 0%, rgba(5,5,5,0.65) 40%, rgba(5,5,5,0.95) 100%)' }}
                        data-testid="audit-gate"
                      >
                        <div
                          className="w-full max-w-md mx-4 rounded-2xl border p-8 text-center"
                          style={{ borderColor: 'rgba(226,193,121,0.3)', background: 'rgba(10,10,10,0.94)', backdropFilter: 'blur(20px)' }}
                        >
                          <Lock size={22} className="mx-auto mb-4" style={{ color: '#e2c179' }} />
                          <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Syne', sans-serif" }} data-testid="audit-gate-title">
                            {t('auditGateTitle')}
                          </h3>
                          <p className="text-sm text-gray-400 mb-6">{t('auditGateText')}</p>
                          <form onSubmit={unlockReport} noValidate className="space-y-3">
                            <input
                              type="email"
                              value={leadEmail}
                              onChange={(e) => { setLeadEmail(e.target.value); if (leadStatus === 'error') setLeadStatus('idle'); }}
                              placeholder={t('auditGatePlaceholder')}
                              className="form-input text-center"
                              data-testid="audit-gate-email-input"
                            />
                            <button
                              type="submit"
                              className="btn-accent w-full justify-center"
                              disabled={leadStatus === 'sending'}
                              data-testid="audit-gate-submit-btn"
                            >
                              {leadStatus === 'sending' ? '...' : t('auditGateBtn')}
                            </button>
                          </form>
                          {leadStatus === 'error' && (
                            <p className="mt-3 text-xs" style={{ color: '#fca5a5' }} data-testid="audit-gate-error">
                              {t('auditGateError')}
                            </p>
                          )}
                          <p className="mt-4 text-[11px] text-gray-600">{t('auditGateNote')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </AnimateIn>
      </div>
    </section>
  );
};

export default AuditSection;
